import Phaser from 'phaser';
import { appConfig } from '@config/app.config';
import { animationConfig, animationEase } from '@config/animation.config';
import { gameBalance } from '@config/gameBalance';
import { getBootstrap } from '@core/bootstrap/GameBootstrap';
import { ServiceTokens } from '@core/bootstrap/ServiceRegistry';
import type { LocalizationService } from '@localization/LocalizationService';
import type { GameManager } from '@gameplay/GameManager';
import type { BanknoteInstance } from '@gameplay/banknotes/models/BanknoteTypes';
import { GameplayAnimations } from '@ui/animations/GameplayAnimations';
import { delayPromise } from '@ui/animations/tweenUtils';
import { BanknoteView, type BanknoteDimensions, type BanknoteViewResult } from '@ui/components/BanknoteView';
import {
  computeGridSlots,
  gridDimensions,
  previewBanknotePosition,
  previewDimensions,
} from '@ui/gameplay/gridLayout';
import { sceneKeys } from '@utils/constants';

type RoundPhase = 'preview' | 'grid' | 'resolving';

/**
 * Oyun sahnesi — görsel akış; mantık GameManager'da.
 */
export class GameplayScene extends Phaser.Scene {
  private gameManager!: GameManager;
  private localization!: LocalizationService;
  private scoreText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private fxLayer!: Phaser.GameObjects.Container;
  private noteViews: BanknoteViewResult[] = [];
  private phase: RoundPhase = 'preview';
  private inputLocked = false;
  private flowGeneration = 0;
  private activePreviewTimer: Phaser.Time.TimerEvent | null = null;

  constructor() {
    super(sceneKeys.gameplay);
  }

  create(): void {
    const { registry } = getBootstrap();
    this.gameManager = registry.get<GameManager>(ServiceTokens.gameManager);
    this.localization = registry.get<LocalizationService>(ServiceTokens.localization);

    this.cameras.main.setBackgroundColor('#12151c');
    this.cameras.main.fadeIn(220, 0, 0, 0);
    this.inputLocked = false;
    this.flowGeneration = 0;

    this.scoreText = this.add
      .text(appConfig.gameWidth / 2, 120, '', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '36px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.hintText = this.add
      .text(appConfig.gameWidth / 2, 200, '', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '28px',
        color: '#a8b0c0',
      })
      .setOrigin(0.5);

    this.fxLayer = this.add.container(0, 0);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cancelPreviewTimer();
      this.clearNotes();
      this.tweens.killAll();
    });

    this.updateScoreDisplay();
    this.gameManager.startRun();
    void this.runRoundFlow();
  }

  private async runRoundFlow(): Promise<void> {
    const generation = this.flowGeneration;

    await this.playPreviewPhase(generation);
    if (!this.isFlowActive(generation)) {
      return;
    }

    await this.playGridPhase(generation);
  }

  private async playPreviewPhase(generation: number): Promise<void> {
    this.phase = 'preview';
    this.clearNotes();

    const target = this.gameManager.getTargetBanknote();
    if (!target) {
      return;
    }

    await GameplayAnimations.fadeHint(
      this,
      this.hintText,
      this.localization.t('game.remember'),
    );
    this.resetHintStyle();

    if (!this.isFlowActive(generation)) {
      return;
    }

    const view = this.createBanknoteView(target, previewBanknotePosition.x, previewBanknotePosition.y, previewDimensions, false);
    await GameplayAnimations.previewEnter(this, view);

    if (!this.isFlowActive(generation)) {
      return;
    }

    await new Promise<void>((resolve) => {
      this.activePreviewTimer = this.time.delayedCall(gameBalance.previewDurationMs, () => {
        this.activePreviewTimer = null;
        resolve();
      });
    });

    if (!this.isFlowActive(generation)) {
      return;
    }

    await GameplayAnimations.previewExit(this, view);
    this.destroyView(view);
    this.gameManager.completePreview();
  }

  private async playGridPhase(generation: number): Promise<void> {
    this.phase = 'grid';
    this.inputLocked = false;

    await GameplayAnimations.fadeHint(
      this,
      this.hintText,
      this.localization.t('game.select'),
    );

    if (!this.isFlowActive(generation)) {
      return;
    }

    const grid = this.gameManager.getGridBanknotes();
    const slots = computeGridSlots();

    const views = grid.map((banknote, index) => {
      const slot = slots[index];
      if (!slot) {
        throw new Error(`Grid slot missing for index ${index}`);
      }

      const view = this.createBanknoteView(banknote, slot.x, slot.y, gridDimensions, true);
      view.hitArea.on('pointerdown', () => this.handleSelection(banknote.instanceId, view, generation));
      return view;
    });

    await GameplayAnimations.gridEnter(this, views);
  }

  private handleSelection(banknoteId: string, view: BanknoteViewResult, generation: number): void {
    if (this.inputLocked || this.phase !== 'grid' || !this.isFlowActive(generation)) {
      return;
    }

    this.inputLocked = true;
    this.phase = 'resolving';

    const result = this.gameManager.selectBanknote(banknoteId);
    const others = this.noteViews.filter((note) => note.banknoteId !== banknoteId);

    if (result === 'correct') {
      this.showFeedbackHint(this.localization.t('game.correct'), '#5ddea8');
      const overlay = GameplayAnimations.playCorrect(this, view, others, this.scoreText);
      this.fxLayer.add(overlay);
      this.updateScoreDisplay(true);

      void delayPromise(this, animationConfig.correctBounceMs + animationConfig.correctRoundGapMs).then(
        () => {
          if (!this.isFlowActive(generation)) {
            return;
          }

          this.clearNotes();
          this.gameManager.prepareNextRound();
          void this.runRoundFlow();
        },
      );
      return;
    }

    if (result === 'wrong') {
      this.showFeedbackHint(this.localization.t('game.wrong'), '#ff7b7b');
      const correctId = this.gameManager.getRoundState().correctInstanceId;
      const correctView = this.noteViews.find((note) => note.banknoteId === correctId);

      if (correctView) {
        GameplayAnimations.playWrong(this, view, correctView, this.noteViews);
      }

      void delayPromise(this, animationConfig.wrongHoldMs).then(() => {
        if (!this.isFlowActive(generation)) {
          return;
        }

        this.flowGeneration += 1;
        this.scene.start(sceneKeys.gameOver, { score: this.gameManager.score });
      });
    }
  }

  private createBanknoteView(
    banknote: BanknoteInstance,
    x: number,
    y: number,
    dimensions: BanknoteDimensions,
    interactive: boolean,
  ): BanknoteViewResult {
    const view = BanknoteView.create(this, x, y, banknote, dimensions, interactive);
    this.noteViews.push(view);
    return view;
  }

  private destroyView(view: BanknoteViewResult): void {
    this.tweens.killTweensOf(view.container);
    this.tweens.killTweensOf(view.hitArea);
    view.container.destroy();
    view.hitArea.destroy();
    this.noteViews = this.noteViews.filter((item) => item !== view);
  }

  private showFeedbackHint(text: string, color: string): void {
    this.hintText.setText(text);
    this.hintText.setColor(color);
    this.tweens.add({
      targets: this.hintText,
      scale: 1.08,
      duration: 140,
      yoyo: true,
      ease: animationEase.outBack,
    });
  }

  private resetHintStyle(): void {
    this.hintText.setColor('#a8b0c0');
    this.hintText.setScale(1);
  }

  private updateScoreDisplay(animate = false): void {
    this.scoreText.setText(this.localization.t('game.score', { score: this.gameManager.score }));

    if (!animate) {
      this.scoreText.setScale(1);
    }
  }

  private clearNotes(): void {
    this.noteViews.forEach((view) => {
      this.tweens.killTweensOf(view.container);
      this.tweens.killTweensOf(view.hitArea);
      view.container.destroy();
      view.hitArea.destroy();
    });
    this.noteViews = [];
    this.fxLayer.removeAll(true);
  }

  private cancelPreviewTimer(): void {
    this.activePreviewTimer?.remove(false);
    this.activePreviewTimer = null;
  }

  private isFlowActive(generation: number): boolean {
    return generation === this.flowGeneration && this.scene.isActive();
  }
}
